// In-memory коллектор двух групп таймингов:
// (A) фазы загрузки страницы — DCL / load / FCP + custom-точки Phaser / карта.
// (B) rotate-цикл — от orientationchange до последнего relayoutMap.
//
// Данные хранятся в module-scope, обновляют подписчиков через onPerfUpdate.
// Никакого сетевого sink'а нет: dev-сборка рисует в overlay
// (см. DevPerfOverlay.ts), в prod модуль вообще не создаёт побочных эффектов
// сверх PerformanceObserver — который дёшев и всё равно полезен для W14
// аналитики (тогда сюда добавится posthog.capture в emit()).

export type PagePhase = 'dcl' | 'load' | 'fcp' | 'phaser' | 'map';

interface PerfSnapshot {
  pageMs: Partial<Record<PagePhase, number>>;
  lastRotateMs?: number;
}

const state: PerfSnapshot = { pageMs: {} };
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

function setPageMs(phase: PagePhase, ms: number): void {
  state.pageMs[phase] = Math.round(ms);
  emit();
}

// PerformanceNavigationTiming — единый источник для DCL/load. startTime=0
// у navigation, поэтому domContentLoadedEventEnd / loadEventEnd = миллисекунды
// от navigationStart, что нам и нужно. Значения = 0 когда событие ещё не произошло.
function collectNavigationTiming(): void {
  const nav = performance.getEntriesByType('navigation')[0] as
    | PerformanceNavigationTiming
    | undefined;
  if (!nav) return;
  if (nav.domContentLoadedEventEnd > 0) setPageMs('dcl', nav.domContentLoadedEventEnd);
  if (nav.loadEventEnd > 0) setPageMs('load', nav.loadEventEnd);
}

function collectFCP(): void {
  const fcp = performance.getEntriesByName('first-contentful-paint')[0];
  if (fcp) setPageMs('fcp', fcp.startTime);
}

if (typeof window !== 'undefined') {
  collectNavigationTiming();
  collectFCP();

  // FCP может прийти уже после импорта модуля (main.ts грузится синхронно, до
  // первого paint'а). PerformanceObserver с buffered=true ловит и уже случившийся,
  // и будущий FCP. Try/catch — на случай старых браузеров без PaintTiming.
  if ('PerformanceObserver' in window) {
    try {
      const po = new PerformanceObserver((entries) => {
        for (const e of entries.getEntries()) {
          if (e.name === 'first-contentful-paint') setPageMs('fcp', e.startTime);
        }
      });
      po.observe({ type: 'paint', buffered: true });
    } catch {
      // no-op: fallback через getEntriesByName выше уже сработал.
    }
  }

  // loadEventEnd заполнится ПОСЛЕ window 'load' — перечитываем.
  window.addEventListener('load', collectNavigationTiming);
}

export function markPhaserCreated(): void {
  setPageMs('phaser', performance.now());
}

export function markMapFirstReady(): void {
  if (state.pageMs.map !== undefined) return; // фиксируем только первый заход
  setPageMs('map', performance.now());
}

// Rotate-цикл: matchMedia/screen.orientation/orientationchange могут стрелять
// подряд — фиксируем первую точку, дальше игнорим следующие стартеры в течение
// ROTATE_WINDOW_MS. lastRotateMs обновляется на КАЖДЫЙ relayout в этом окне
// (iOS-шторм даёт 5-15 resize за ~2 сек), финальное значение = когда карта
// окончательно застыла.
const ROTATE_WINDOW_MS = 3000;
let rotateStartAt: number | undefined;

export function markRotateStart(): void {
  if (rotateStartAt !== undefined) return;
  rotateStartAt = performance.now();
  window.setTimeout(() => {
    rotateStartAt = undefined;
  }, ROTATE_WINDOW_MS);
}

export function markMapReadyAfterRotate(): void {
  if (rotateStartAt === undefined) return;
  state.lastRotateMs = Math.round(performance.now() - rotateStartAt);
  emit();
}

export function getPerfSnapshot(): PerfSnapshot {
  return {
    pageMs: { ...state.pageMs },
    lastRotateMs: state.lastRotateMs,
  };
}

export function onPerfUpdate(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}
